<template>
  <div class="main-wrapper" :style="backgroundImage">
    <!-- Replacing q-header -->
    <header
      :class="{ reveal: userStore.isLoggedIn }"
      class="hud"
      v-if="userStore.isLoggedIn && userStore.isLoggedIn"
    >
      <transition appear>
        <nav
          v-if="!gamePlayStore.currentGame"
          class="main-screen"
          aria-label="Main Screen"
        >
          <button
            v-if="isOnUserStats"
            class="go-games pixel-border"
            type="button"
            @click="router.push({ name: 'home' })"
            aria-label="Go to games"
          >
            <span class="pixel">Go to games</span>
          </button>

          <router-link
            v-else
            class="welcome-link"
            :to="{ name: 'user-stats' }"
            title="View progress"
            aria-label="View progress"
          >
            <h4>Welcome, {{ userStore.user?.first_name || '...' }}</h4>
          </router-link>
          <div class="meta-navigation">
            <button
              :class="{
                'in-mission': gameStore.inMission,
                'free-play': !gameStore.inMission,
              }"
              @mouseenter="handleModeHoverEnter"
              @mouseleave="handleModeHoverLeave"
              @touchstart.passive="playModeVoice"
              @focus="showMissionModeModal = true"
              @blur="showMissionModeModal = false"
            >
              <span>
                {{ gameStore.inMission ? 'Mission Mode' : 'Free Play' }}
              </span>
            </button>
            <button
              class="tickets"
              :class="{ shake: userStore.user?.ticketsUpdated }"
              ref="tickets"
            >
              <span
                ><AnimatedNumber
                  :value="gameProgressStore.tickets"
                  :round="1"
                  :duration="300"
              /></span>
            </button>
            <button class="games pixel-border no-cursor">
              <span class="pixel"
                >{{ userStore.user?.completed_plays || 0 }} Games Today</span
              >
            </button>

            <button
              class="mute"
              :class="{ isMuted: gameAudioStore.isMasterMuted }"
              @click="toggleSound()"
            >
              Toggle Sound
            </button>

            <button @click="openModal" class="logout pixel-border">
              <span class="pixel-inverse">Logout</span>
            </button>
          </div>
        </nav>

        <!-- In-Game Navigation -->
        <nav v-else class="in-game" aria-label="In Game Navigation">
          <template
            v-if="
              !['introduction', 'loading', 'instructions'].includes(
                uiStore.state.currentStep
              )
            "
          >
            <button class="level">
              <span v-if="gamePlayStore.level">
                Level
                <span
                  ><AnimatedNumber :value="gamePlayStore.level" :duration="300"
                /></span>
              </span>
            </button>

            <div class="hints colored">
              <div class="the-hints">
                <div
                  v-if="
                    gamePlayStore.textPlayHints &&
                    gamePlayStore.textPlayHints !== ''
                  "
                  class="hint-text"
                >
                  <h4 v-html="gamePlayStore.textPlayHints.toUpperCase()"></h4>
                </div>
                <div
                  class="hints-color"
                  :class="{
                    colorHintHasText: gamePlayStore.textPlayHints,
                  }"
                  v-if="gamePlayStore.hints"
                >
                  <span
                    v-for="(hint, index) in gamePlayStore.hints"
                    :key="index"
                    class="hint-color"
                    :class="hint"
                  ></span>
                </div>
              </div>
              <span class="the-count"
                ><AnimatedNumber :value="gamePlayStore.score" :duration="300"
              /></span>
            </div>

            <div class="trial">
              <span
                v-for="(trial, index) in gamePlayStore.trials"
                :key="index"
                :class="gamePlayStore.getTrialClasses(index)"
              ></span>
              <h4>TRIALS</h4>
            </div>

            <div class="round">
              <span
                v-for="(round, index) in gamePlayStore.rounds"
                :key="index"
                :class="{
                  active: index === gamePlayStore.currentRound - 1,
                }"
              ></span>
              <h4>ROUNDS</h4>
            </div>
          </template>
          <template v-else>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </template>
          <div class="meta-navigation">
            <button
              class="mute"
              :class="{ isMuted: gameAudioStore.isMasterMuted }"
              @click="toggleSound()"
            >
              Toggle Sound
            </button>
            <button
              class="tickets"
              :class="{ tada: userStore.user?.ticketsUpdated }"
              v-if="gameProgressStore.tickets !== null"
            >
              <span>
                <AnimatedNumber
                  :value="gameProgressStore.tickets"
                  :duration="300"
              /></span>
            </button>

            <button
              v-if="gamePlayStore.currentGame"
              @click="handleLeaveGame"
              class="leave-game logout pixel-border"
              :class="{ 'is-leaving': isLeavingGame }"
              :disabled="isLeavingGame"
            >
              <span class="pixel-inverse">{{
                isLeavingGame ? '...' : 'X'
              }}</span>
            </button>

            <button v-else @click="openModal()" class="logout pixel-border">
              <span class="pixel-inverse">Logout</span>
            </button>
          </div>
        </nav>
      </transition>
    </header>

    <!-- Overlay and Modals -->
    <div id="overlay" v-if="modalStore.modalMessage?.title"></div>
    <transition name="mode-hover">
      <div
        v-if="showMissionModeModal"
        class="mode-hover-card"
        :class="{
          'is-mission': gameStore.inMission,
          'is-free-play': !gameStore.inMission,
        }"
      >
        <div class="mode-hover-card__content">
          <h3>{{ modeTooltipTitle }}</h3>
          <p class="body">{{ modeTooltipBody }}</p>
        </div>
      </div>
    </transition>

    <transition name="fade"
      ><Modal
        v-if="modalStore.isVisible"
        :message="modalStore.message"
        :isLevelUpModal="false"
      ></Modal
    ></transition>
    <transition name="fade"
      ><Modal
        v-if="modalStore.isLevelUpModalVisible"
        :message="modalStore.levelUpMessage"
        :isLevelUpModal="true"
    /></transition>

    <div v-if="showLogoutModal" class="modal modal-message">
      <div class="content">
        <h1>Confirm Logout</h1>
        <p class="body">Do you really want to logout?</p>
        <div class="modal-action">
          <button class="modal_action_button" @click="confirmModal()">
            Yes
          </button>
          <button class="modal_action_button" @click="closeModal()">No</button>
        </div>
      </div>
    </div>

    <!-- Main Content (Replacing q-page-container) -->
    <div
      :class="['page-container', { 'blur-content': modalStore.displayModal }]"
      :style="pageContainerStyles"
    >
      <router-view v-slot="{ Component }">
        <transition appear>
          <component :is="Component" />
        </transition>
      </router-view>
      <PositiveFeedback />
    </div>

    <!-- Universal Error Screen -->
    <ErrorScreen />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, RouterView } from 'vue-router';
import Modal from '../components/GamePlay/Common/Modal.vue';
import ErrorScreen from '../components/GamePlay/Common/ErrorScreen.vue';
import { useGamePlayStore } from '@/stores/gamePlayStore';
import { useUserStore } from '@/stores/userStore';
import { useModalStore } from '@/stores/modalStore';
import { useThemeStore } from '@/stores/themeStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useUIStore } from '@/stores/uiStore';
import { useGameAudioStore } from '@/stores/gameAudioStore';
import { useGameProgressStore } from '@/stores/gameProgressStore';
import { useGameStore } from '@/stores/gameStore';
import { useScreenSafeArea } from '@vueuse/core';
import AnimatedNumber from '@/components/Layout/AnimatedNumber.vue';
import PositiveFeedback from '@/components/GamePlay/Feedback/PositiveFeedback.vue';

const gamePlayStore = useGamePlayStore();
const userStore = useUserStore();
const modalStore = useModalStore();
const sessionStore = useSessionStore();
const uiStore = useUIStore();
const gameAudioStore = useGameAudioStore();
const gameProgressStore = useGameProgressStore();
const gameStore = useGameStore();

const safeArea = useScreenSafeArea();
const HUD_GLOBAL_TOP_OFFSET_PX = 20;

const router = useRouter();

const isOnUserStats = computed(
  () => router.currentRoute.value.name === 'user-stats'
);

const showMissionModeModal = ref(false);
const showLogoutModal = ref(false);
const isLeavingGame = ref(false);

const openModal = () => {
  showLogoutModal.value = true;
};

const closeModal = () => {
  showLogoutModal.value = false;
};

const confirmModal = () => {
  closeModal();
  router.push('/logout');
};

const SHARED_AUDIO = '/assets/games/audio/shared';

const playModeVoice = async () => {
  await gameAudioStore.handleUserInteraction();
  const clip = gameStore.inMission ? 'mission-mode.mp3' : 'free-play.mp3';
  gameAudioStore.playSpeech(`${SHARED_AUDIO}/${clip}`);
};

const modeTooltipTitle = computed(() =>
  gameStore.inMission ? 'Mission Mode' : 'Free Play'
);

const modeTooltipBody = computed(() =>
  gameStore.inMission
    ? 'Complete these three games to unlock the full arcade.'
    : 'Mission complete. You can now play the full game list freely.'
);

const handleModeHoverEnter = () => {
  showMissionModeModal.value = true;
  playModeVoice();
};

const handleModeHoverLeave = () => {
  showMissionModeModal.value = false;
};

const toggleSound = () => {
  try {
    gameAudioStore.toggleMasterMute();
  } catch (error) {
    console.warn('Error toggling audio mute:', error);
  }
};

const handleLeaveGame = async () => {
  console.log('[MainWrapper] Leave game button clicked');
  if (isLeavingGame.value) {
    console.log('[MainWrapper] Already leaving game, ignoring click');
    return;
  }

  isLeavingGame.value = true;
  try {
    await gamePlayStore.leaveGame();
  } catch (error) {
    console.error('[MainWrapper] Error leaving game:', error);
  } finally {
    // Reset the state after a short delay to ensure navigation has completed
    setTimeout(() => {
      isLeavingGame.value = false;
    }, 500);
  }
};

const pageContainerStyles = computed(() => {
  return {
    paddingTop: uiStore.state.currentStep === 'instructions' ? '0' : '',
    filter: `blur(${sessionStore.displayModal ? '5px' : '0px'})`,
  };
});

const safeAreaStyle = computed(() => {
  const parsedTopInset = Number.parseFloat(`${safeArea.top.value}`);
  const hudSafeAreaTopBase = Number.isFinite(parsedTopInset)
    ? Math.max(parsedTopInset - 6, 0)
    : 0;
  const hudSafeAreaTop = hudSafeAreaTopBase + HUD_GLOBAL_TOP_OFFSET_PX;

  return {
    '--hud-safe-area-top': `${hudSafeAreaTop}px`,
    paddingTop: '0px',
    paddingBottom: `${safeArea.bottom.value}`,
    paddingLeft: `${safeArea.left.value}`,
    paddingRight: `${safeArea.right.value}`,
  };
});

const themeStore = useThemeStore();

const backgroundImage = computed(() => {
  // Create base styles
  const baseStyles = {
    backgroundImage: themeStore.theme.backgroundImage
      ? `url(${themeStore.theme.backgroundImage})`
      : '',
    ...safeAreaStyle.value,
  };

  // Only add these properties if backgroundRepeat is explicitly false
  if (themeStore.theme.backgroundRepeat === false) {
    return {
      ...baseStyles,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition:
        gamePlayStore.currentGame === 'PuzzleTrain'
          ? 'center center'
          : 'bottom center',
    };
  }

  // Return base styles if no specific repeat settings are required
  return baseStyles;
});

onMounted(async () => {
  await userStore.checkAuth();

  // Verify ticket consistency after authentication
  if (userStore.isLoggedIn) {
    userStore.verifyTicketConsistency();
    // Check if we need to reset daily games counter
    userStore.checkAndResetDailyGames();
  }

  // Restore mute state from localStorage
  gameAudioStore.restoreMuteStateFromStorage();

  // disable right-click except for certain domains
  document.addEventListener('contextmenu', (event) => {
    let allowedDomains = [
      'staging.domain.com',
      'localhost',
      'incrementalhealth.test',
    ];

    let currentDomain = window.location.hostname;

    if (!allowedDomains.includes(currentDomain)) {
      event.preventDefault();
      console.log('Context menu is disabled');
    }
  });

  // disable Ctrl+R or Command+R reloading
  document.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
      event.preventDefault();
      console.log('Ctrl+R or Command+R is disabled');
    }
  });
});
</script>

<style scoped>
.main-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100svh;
  height: 100dvh;
  width: 100vw;
}
.hud {
  position: relative;
  width: 100%;
}

.page-container {
  margin-top: 0;
  flex: 1;

  /* body is overflow:hidden (game UI), so pages must scroll inside the app shell.
     In a column flex layout, min-height: 0 is required to allow children to
     shrink and use overflow scrolling instead of forcing layout overflow. */
  min-height: 0;
  overflow: hidden;
}

.blur-content {
  filter: blur(5px);
}

.meta-navigation button {
  margin: 0 10px;
}
.mode-hover-card {
  position: fixed;
  top: calc(var(--hud-safe-area-top, 0px) + 84px);
  right: 2vw;
  z-index: 10001;
  width: min(520px, 92vw);
  pointer-events: none;
}

.mode-hover-card__content {
  position: relative;
  background-color: #ffffff;
  color: #000000;
  border-radius: 5px;
  padding: 20px 24px;
  box-shadow: 10px 10px 26px 7px rgba(0, 0, 0, 0.39);
  border-left: 8px solid #800000;
}

.mode-hover-card.is-free-play .mode-hover-card__content {
  border-left-color: #005e80;
}

.mode-hover-card h3 {
  margin: 0 0 10px;
  font-size: 1.5rem;
  font-weight: 700;
}

.mode-hover-card .body {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.45;
}

.mode-hover-enter-active,
.mode-hover-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.28s cubic-bezier(0.2, 0.9, 0.2, 1);
}

.mode-hover-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.mode-hover-leave-to {
  opacity: 0;
  transform: translateX(20px);
  border-radius: 5px;
}

.modal-message .content {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  width: 300px;
  margin: auto;
}
</style>
